import React from 'react';
import { EDX_COLORS, EDX_LABELS, EDX_TEXT_COLORS } from '../../constants/edxDescriptions';
import {
  EDX_BANDS_FULL,
  BRITTLENESS_BANDS_FULL,
  brittlenessBandIndex,
} from '../../constants/helpScores';

/**
 * Personalised "How to read the scores" primer + EDX / Brittleness tables
 * (handoff 30 Item 2). Ported from RB-UX dashboard.js / prototype shell.js.
 *
 * @param {object} site — { name, band, edx, ehi, britt, brittCat, proc, limiting, limLevel }
 */
export default function HowToReadScores({ site }) {
  if (!site) return null;
  const band = site.band != null ? Number(site.band) : null;
  const bandName = band != null ? (EDX_LABELS[band - 1] || '') : '';
  const edxStr = site.edx != null ? String(site.edx) : (band != null ? String(band) : '—');
  const britt = site.britt != null ? Number(site.britt) : null;
  const brittCat = site.brittCat || '—';
  const proc = site.proc || {};
  const limiting = site.limiting || '—';
  const limLevel = site.limLevel != null ? site.limLevel : '—';
  const brittIdx = brittlenessBandIndex(britt);

  return (
    <div className="hs-block" id="help-scores" data-tour="help-scores">
      <div className="hs-eyebrow">Personalised · {site.name || 'this property'}</div>
      <h4 id="hs-top">How to read the scores</h4>
      <p className="hs-lead">A short primer on the scoring behind every number.</p>

      <div className="hs-sec">
        <div className="hs-h">The Land Health Score (EDX)</div>
        <p>
          The Land Health Score is the 1 to 9 level plus the band name.
          Your property: <b>Land Health {edxStr} · {bandName || '—'}</b>.
        </p>
        <p className="hs-sec-note hs-note">
          The four processes form a cascade: water → energy → mineral → community.
          Water must be captured before plants photosynthesise; plants before nutrients cycle;
          nutrients before diverse, resilient communities establish.
        </p>
        <EdxBandsTable currentBand={band} edxStr={edxStr} bandName={bandName} />
      </div>

      <div className="hs-sec">
        <div className="hs-h">Ecosystem process scores</div>
        <p>
          Each process sits from 1 (collapsed, centre of the kite) to 9 (flourishing) relative to its full functional range.
          Yours: Water {proc.WCI ?? '—'}% · Energy {proc.EFI ?? '—'}% · Mineral {proc.MCI ?? '—'}% · Community {proc.CDI ?? '—'}%.
          Read together they reveal what is doing the heavy lifting and what holds the system back
          {limiting !== '—' ? <> — here <b>{limiting}</b>.</> : '.'}
        </p>
      </div>

      <div className="hs-sec">
        <div className="hs-h">Brittleness</div>
        <p>
          Brittleness (1–10) describes how the environment responds to rest.
          Non-brittle (1–4): resting land recovers through biological decomposition.
          Brittle (6–10): rest leads to oxidation; the land needs periodic disturbance (such as grazing) to cycle nutrients.
          Your site: <b>{britt != null ? `${britt}/10` : '—'} ({brittCat})</b>.
        </p>
        <BrittlenessTable currentIndex={brittIdx} score={britt} category={brittCat} />
      </div>

      <div className="hs-sec">
        <div className="hs-h">The diagnostic matrix</div>
        <p>
          Three layers: process <b>triage</b> (which process limits), contextual <b>attribution</b>
          {' '}(why, given brittleness and farming system), and system-specific <b>prescription</b> (the first move).
          {limiting !== '—' ? (
            <> For your site the triage points to <b>{limiting}</b>
              {limLevel !== '—' ? (
                Number(limLevel) > 9 ? <> at {limLevel}%.</> : <> at Level {limLevel}.</>
              ) : '.'}
            </>
          ) : null}
        </p>
      </div>
    </div>
  );
}

function ProcCell({ pair }) {
  return (
    <td className="hs-pc">
      <b>{pair[0]}</b>
      <span className="hs-sub">{pair[1]}</span>
    </td>
  );
}

function EdxBandsTable({ currentBand, edxStr, bandName }) {
  return (
    <>
      <div className="hs-tablewrap">
        <table className="hs-table hs-edx">
          <thead>
            <tr>
              <th>Land Health Score</th>
              <th>Description</th>
              <th>Water Cycle (WCI)</th>
              <th>Mineral Cycle (MCI)</th>
              <th>Energy Flow (EFI)</th>
              <th>Community Dynamics (CDI)</th>
            </tr>
          </thead>
          <tbody>
            {EDX_BANDS_FULL.map((r) => {
              const lv = r[0];
              const you = lv === currentBand;
              const color = EDX_COLORS[lv - 1];
              return (
                <tr key={lv} className={you ? 'hs-you' : undefined}>
                  <th scope="row" className="hs-lv">
                    <span
                      className="hs-lvchip"
                      style={{ background: color, color: EDX_TEXT_COLORS[lv] || '#fff' }}
                    >
                      {lv}
                    </span>
                    {EDX_LABELS[lv - 1]}
                    {you ? <span className="hs-you-tag">◄ your site</span> : null}
                  </th>
                  <td className="hs-desc">{r[3]}</td>
                  <ProcCell pair={r[4]} />
                  <ProcCell pair={r[5]} />
                  <ProcCell pair={r[6]} />
                  <ProcCell pair={r[7]} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {currentBand != null ? (
        <p className="hs-legend">
          Your site sits at <b>Land Health {edxStr} · {bandName}</b>
          {' '}(row marked ◄).
        </p>
      ) : null}
    </>
  );
}

function BrittlenessTable({ currentIndex, score, category }) {
  return (
    <>
      <div className="hs-tablewrap">
        <table className="hs-table hs-britt">
          <thead>
            <tr>
              <th>Brittleness Class</th>
              <th>Definition</th>
              <th>Field Validation</th>
              <th>Management Implication</th>
            </tr>
          </thead>
          <tbody>
            {BRITTLENESS_BANDS_FULL.map((r, i) => {
              const you = i === currentIndex;
              return (
                <tr key={r[2]} className={you ? 'hs-you' : undefined}>
                  <th scope="row" className="hs-bc">
                    {Number(r[0]).toFixed(1)}–{Number(r[1]).toFixed(1)} · {r[2]}
                    {you ? <span className="hs-you-tag">◄ your site</span> : null}
                  </th>
                  <td>{r[3]}</td>
                  <td>{r[4]}</td>
                  <td>{r[5]}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {score != null ? (
        <p className="hs-legend">
          Your site: <b>{score}/10 ({category})</b> — row marked ◄.
        </p>
      ) : null}
    </>
  );
}
